import { useEffect } from 'react';

import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import type { AppLocale } from '../app/config';
import { usePersistentState } from '../hooks/usePersistentState';
import { initAnalytics } from '../lib/analytics';
import { createStorageKey } from '../lib/storage';

export type CookieConsent = 'accepted' | 'declined';

const cookieConsentStorageKey = createStorageKey('app.cookieConsent');

function normalizeConsent(
  value: unknown,
  fallback: CookieConsent | null
): CookieConsent | null {
  return value === 'accepted' || value === 'declined' ? value : fallback;
}

type CookieNoticeProps = {
  currentLocale: AppLocale;
};

export function CookieNotice({ currentLocale }: CookieNoticeProps) {
  const { t } = useTranslation();
  const [consent, setConsent] = usePersistentState<CookieConsent | null>(
    cookieConsentStorageKey,
    null,
    { deserialize: normalizeConsent }
  );

  useEffect(() => {
    if (consent === 'accepted') {
      initAnalytics();
    }
  }, [consent]);

  if (consent !== null) {
    return null;
  }

  return (
    <div className='cookie-notice' role='region' aria-label={t('cookies.ariaLabel')}>
      <div className='cookie-notice-text'>
        <i className='fas fa-cookie-bite' aria-hidden='true' />{' '}
        {t('cookies.text')}{' '}
        <Link to={`/${currentLocale}/privacy`}>{t('footer.privacy')}</Link>.
      </div>
      <div className='cookie-notice-actions'>
        <button
          className='btn btn-primary btn-sm'
          onClick={() => setConsent('accepted')}
          type='button'
        >
          <i className='fas fa-check' aria-hidden='true' />{' '}
          {t('cookies.accept')}
        </button>
        <button
          className='btn btn-secondary btn-sm'
          onClick={() => setConsent('declined')}
          type='button'
        >
          {t('cookies.decline')}
        </button>
      </div>
    </div>
  );
}
